// lib/schema.ts
import type { WithContext, IndividualPhysician, PostalAddress, MedicalSpecialty } from 'schema-dts';
import { Pages } from '.generated/Project.CW.model';
import { getBaseUrl } from 'lib/utils/get-base-url';

export function toAbsoluteUrl(rawUrl?: string): string | undefined {
  if (!rawUrl) return undefined;
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  const base = getBaseUrl();
  return `${base.replace(/\/$/, '')}/${rawUrl.replace(/^\//, '')}`;
}

/**
 * CMS fields may be:
 * - raw values (string, number, etc.)
 * - wrapper objects like { value: T }
 */
export function safeFieldValue<T>(field: { value?: T } | T | null | undefined): T | undefined {
  if (field == null) return undefined;
  if (typeof field === 'object' && 'value' in field) return field.value;
  return field as T;
}

/** Extract either a `.name` property or a `.value`/raw value safely */
export function extractNameOrValue(field: unknown): string | undefined {
  if (field == null) return undefined;

  if (typeof field === 'string') return field;

  if (typeof field === 'object') {
    // If object has a name property, prefer it
    if ('name' in (field as Record<string, unknown>)) {
      const rawName = (field as Record<string, unknown>).name;
      if (typeof rawName === 'string') return rawName;
      // If name itself is a field wrapper
      if (
        typeof rawName === 'object' &&
        rawName != null &&
        'value' in (rawName as Record<string, unknown>)
      ) {
        return safeFieldValue<string>(rawName as { value?: string });
      }
    }

    // Otherwise try value wrapper
    if ('value' in (field as Record<string, unknown>)) {
      return safeFieldValue<string>(field as { value?: string });
    }
  }

  return undefined;
}

/** Specialty types coming from CMS */
type Specialty = string | { name?: string } | { value?: string };

/** Minimal structural type for locations */
type LocationLike = {
  name?: string;
  url?: string;
  fields?: {
    PageType?: { name?: string; id: string };
    locationName?: string | { value?: string };
    businessListingName?: string | { value?: string };
    website?: string | { value?: string };
    address1?: string | { value?: string };
    city?: string | { value?: string };
    state?: string | { value?: string };
    addressRegion?: string | { value?: string };
    zip?: string | { value?: string };
    addressCountry?: string | { value?: string };
    mobileVisiblePhoneNumber?: string | { value?: string };
    phone?: string | { value?: string };
  };
};

/** Optional top‑level fields that may exist on ProviderProfile */
type RawSpecialtiesHolder = { specialties?: Specialty[] };
type RawLocationsHolder = { locations?: LocationLike[] };

function formatTelephone(raw?: string): string | undefined {
  if (!raw) return undefined;
  const digits = raw.replace(/\D+/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (raw.startsWith('+')) return raw;
  return raw;
}

function stripQueryParams(url?: string): string | undefined {
  if (!url) return undefined;
  const idx = url.indexOf('?');
  return idx === -1 ? url : url.substring(0, idx);
}

function extractSpecialty(s: Specialty): string | undefined {
  if (typeof s === 'string') return s;
  if ('name' in s && typeof s.name === 'string') return s.name;
  return safeFieldValue<string>(s as { value?: string });
}

export function buildProviderJsonLd(
  data: Pages.ProviderProfile,
  pageUrl: string
): WithContext<IndividualPhysician> {
  const firstName = safeFieldValue<string>(data.fields?.firstName) ?? '';
  const lastName = safeFieldValue<string>(data.fields?.lastName) ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  const photoField = safeFieldValue<{ src?: string } | string>(data.fields?.photo);
  const rawImage = typeof photoField === 'object' ? photoField?.src : photoField;
  const image = stripQueryParams(rawImage);

  // Specialties may appear in two places
  const specialtiesSource = Array.isArray((data as RawSpecialtiesHolder).specialties)
    ? (data as RawSpecialtiesHolder).specialties
    : Array.isArray(data.fields?.specialties)
      ? data.fields.specialties
      : undefined;

  const medicalSpecialty: MedicalSpecialty[] | undefined = Array.isArray(specialtiesSource)
    ? specialtiesSource
        .map(extractSpecialty)
        .filter((v): v is string => Boolean(v))
        .map((name) => ({ '@type': 'MedicalSpecialty', name }) as MedicalSpecialty)
    : undefined;

  // Locations may appear in two places
  const locations: LocationLike[] = Array.isArray(data.fields?.locations)
    ? data.fields.locations
    : Array.isArray((data as RawLocationsHolder).locations)
      ? (data as RawLocationsHolder).locations!
      : [];

  const practicesAt = locations
    .map((loc: LocationLike) => {
      const locFields = loc.fields ?? {};

      // Extract page type name safely (string, { name }, or { value })
      const pageTypeName = extractNameOrValue(locFields.PageType);

      const isHospital = typeof pageTypeName === 'string' && /hospital/i.test(pageTypeName);

      const locType = isHospital ? 'Hospital' : 'MedicalClinic';

      const locName =
        loc.name ??
        extractNameOrValue(locFields.locationName) ??
        extractNameOrValue(locFields.businessListingName);

      const locUrl = toAbsoluteUrl(loc.url ?? extractNameOrValue(locFields.website));

      const street = extractNameOrValue(locFields.address1);
      const city = extractNameOrValue(locFields.city);
      const region =
        extractNameOrValue(locFields.state) ?? extractNameOrValue(locFields.addressRegion);
      const postalCode = extractNameOrValue(locFields.zip);
      const country = extractNameOrValue(locFields.addressCountry) ?? 'US';

      const address: PostalAddress | undefined =
        street || city || region || postalCode || country
          ? {
              '@type': 'PostalAddress',
              streetAddress: street,
              addressLocality: city,
              addressRegion: region,
              postalCode: postalCode,
              addressCountry: country,
            }
          : undefined;

      const place: {
        '@type': string;
        '@id'?: string;
        name?: string;
        address?: PostalAddress;
      } = {
        '@type': locType,
        '@id': locUrl,
        name: locName,
      };

      if (address) place.address = address;

      // Deep clone to remove undefined keys
      return JSON.parse(JSON.stringify(place));
    })
    .filter(Boolean);

  // telephone: prefer provider phone, fallback to first location phone
  const rawProviderPhone = safeFieldValue<string>(data.fields?.phone);
  const rawFirstLocPhone = locations[0]
    ? (extractNameOrValue(locations[0].fields?.mobileVisiblePhoneNumber) ??
      extractNameOrValue(locations[0].fields?.phone))
    : undefined;

  const rawTelephone =
    typeof rawProviderPhone === 'string' && rawProviderPhone.trim()
      ? rawProviderPhone
      : typeof rawFirstLocPhone === 'string' && rawFirstLocPhone.trim()
        ? rawFirstLocPhone
        : undefined;

  const telephone = formatTelephone(rawTelephone);

  const isAcceptingNewPatients = (() => {
    const acceptingValue = extractNameOrValue(data.fields?.acceptingNewPatients?.name);
    if (typeof acceptingValue !== 'string') return undefined;

    switch (acceptingValue.toLowerCase()) {
      case 'accepting infants':
      case 'accepting new patients':
        return true;
      case 'not accepting new patients':
        return false;
      default:
        return undefined;
    }
  })();

  const jsonLd: WithContext<IndividualPhysician> = {
    '@context': 'https://schema.org',
    '@type': 'IndividualPhysician',
    '@id': pageUrl,
    name: fullName,
    url: pageUrl,
    image: image,
    medicalSpecialty: medicalSpecialty?.length ? medicalSpecialty : undefined,
    practicesAt: practicesAt.length ? practicesAt : undefined,
    telephone: telephone,
    isAcceptingNewPatients: isAcceptingNewPatients,
    description: safeFieldValue<string>(data.fields?.Description),
  };

  // Remove undefined keys
  return JSON.parse(JSON.stringify(jsonLd)) as WithContext<IndividualPhysician>;
}
